import { useEffect, useState } from 'react';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

interface AgentRow { agentId: string; dev?: any; main?: any; [k: string]: any }

export default function Prompts() {
  const { can } = useAuth();
  const canWrite = can('prompts', 'write');
  const [manifest, setManifest] = useState<any>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<{ agentId: string; branch: string } | null>(null);

  async function load() {
    try { setManifest(await backendApi.get<any>('/admin/prompts')); setError(''); }
    catch (err: any) { setError((err as ApiError).isForbidden ? 'You do not have access to prompts.' : err.message); }
  }
  useEffect(() => { void load(); }, []);

  const agents: AgentRow[] = Array.isArray(manifest) ? manifest
    : manifest?.agents || manifest?.prompts || [];

  return (
    <>
      <header className="page-head"><div><h1>System Prompts</h1>
        <p className="muted">AI prompts per agent, versioned by branch.</p></div></header>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h2 className="card-title">Agents</h2>
        {!manifest ? <p className="muted">Loading…</p> : agents.length === 0 ? (
          <p className="muted">No prompt agents on this tier.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Agent</th><th>dev</th><th>main</th><th>Actions</th></tr></thead>
              <tbody>
                {agents.map((a, i) => {
                  const id = a.agentId || a.name || a.id || String(i);
                  const isEd = (br: string) => editing !== null && editing.agentId === id && editing.branch === br;
                  const devV = typeof a.dev === 'string' ? a.dev : a.dev?.version ? `v${a.dev.version}` : '';
                  const mainV = typeof a.main === 'string' ? a.main : a.main?.version ? `v${a.main.version}` : '';
                  const drift = devV && mainV && devV !== mainV;
                  return (
                    <tr key={id}>
                      <td className="mono">{id}</td>
                      <td className="muted">{devV || '—'}
                        {drift && <span className="badge badge-amber" title="dev is ahead of main" style={{ marginLeft: 6 }}>drift</span>}</td>
                      <td className="muted">{mainV || '—'}</td>
                      <td>
                        <div className="row-actions">
                          {['dev', 'main'].map((br) => (
                            <button key={br} className="btn btn-sm"
                              onClick={() => setEditing(isEd(br) ? null : { agentId: id, branch: br })}>
                              {isEd(br) ? 'Close' : br}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <PromptEditor key={editing.agentId + editing.branch} {...editing} canWrite={canWrite} onSaved={load} />}
    </>
  );
}

interface Version {
  version: string; sha?: string; shortSha?: string; author?: string;
  email?: string; date?: string; subject?: string; isActive?: boolean;
}

function PromptEditor({ agentId, branch, canWrite, onSaved }:
  { agentId: string; branch: string; canWrite: boolean; onSaved: () => void }) {
  const [content, setContent] = useState<string | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  // Version currently loaded into the editor (the "active" pointer for concurrency).
  const [activeVersion, setActiveVersion] = useState<string>('');
  const [viewingVersion, setViewingVersion] = useState<string>('');
  const [error, setError] = useState(''); const [msg, setMsg] = useState('');
  const [note, setNote] = useState(''); // commit note / message sent with the save
  const [busy, setBusy] = useState(false);

  const extract = (d: any) => (typeof d === 'string' ? d : (d.body ?? d.content ?? d.prompt ?? ''));

  useEffect(() => {
    backendApi.get<any>(`/admin/prompts/${encodeURIComponent(agentId)}/${branch}`)
      .then((d) => { setContent(extract(d)); if (d?.version) { setActiveVersion(d.version); setViewingVersion(d.version); } })
      .catch((err) => { setError(err.message); setContent(''); });
    backendApi.get<any>(`/admin/prompts/${encodeURIComponent(agentId)}/${branch}/versions`)
      .then((d) => {
        setVersions(Array.isArray(d) ? d : d.versions || []);
        if (d?.activeVersion) setActiveVersion(d.activeVersion);
      })
      .catch(() => setVersions([]));
  }, [agentId, branch]);

  async function loadVersion(v: Version) {
    const id = v.version ?? v.sha;
    setError(''); setMsg('');
    try {
      const d = await backendApi.get<any>(`/admin/prompts/${encodeURIComponent(agentId)}/${branch}/${id}`);
      setContent(extract(d)); setViewingVersion(id || '');
      setMsg(`Loaded ${id}${id === activeVersion ? ' (active)' : ' — saving will create a new version'}.`);
    } catch (err: any) { setError(err.body?.error || err.message); }
  }

  async function save() {
    if (content == null) return;
    if (content.trim().length < 10) { setError('Refusing to save: body looks too short (min 10 chars).'); return; }
    if (!window.confirm('Push a new prompt version — this triggers a CI redeploy?')) return;
    setBusy(true); setError(''); setMsg('');
    try {
      await backendApi.post(`/admin/prompts/${encodeURIComponent(agentId)}/${branch}`, {
        body: content, note: note.trim() || undefined, expectedActiveVersion: activeVersion || undefined,
      });
      // NOTE: legacy polls /admin/prompts/runs/:branch/:sha for CI status. Not ported —
      // see report. We show a static confirmation instead.
      setMsg('Saved — CI redeploy triggered.'); setNote(''); onSaved();
    } catch (err: any) { setError(err.body?.detail || err.body?.error || err.message); }
    finally { setBusy(false); }
  }

  const charCount = (content ?? '').length;

  return (
    <div className="card">
      <h2 className="card-title">Editing <span className="mono">{agentId}</span> · {branch}</h2>
      {error && <div className="alert alert-error">{error}</div>}
      {msg && <div className="alert alert-ok">{msg}</div>}
      {content === null ? <p className="muted">Loading prompt…</p> : (
        <>
          <p className="muted small">
            Active: <strong className="mono">{activeVersion || '—'}</strong>
            {viewingVersion && viewingVersion !== activeVersion && <> · Viewing: <strong className="mono">{viewingVersion}</strong></>}
            {' · '}{charCount.toLocaleString()} chars
          </p>
          <textarea className="textarea textarea-tall" value={content} readOnly={!canWrite}
            onChange={(e) => setContent(e.target.value)} />
          <p className="muted small" style={{ marginTop: 4 }}>{charCount.toLocaleString()} chars</p>
          {canWrite && (
            <>
              <input style={{ marginTop: 8, width: '100%' }} placeholder="Commit note (optional)"
                value={note} onChange={(e) => setNote(e.target.value)} />
              <button className="btn btn-primary" style={{ marginTop: 10 }} disabled={busy} onClick={save}>
                {busy ? 'Saving…' : 'Save & Push'}</button>
            </>
          )}
          {versions.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 className="detail-h">Version history ({versions.length})</h4>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Version</th><th>Commit</th><th>Subject</th><th>Author</th><th>Date</th><th></th></tr></thead>
                  <tbody>
                    {versions.map((v, i) => {
                      const isActive = v.isActive || v.version === activeVersion;
                      const author = (v.author || '').replace(/<.*$/, '').trim() || '—';
                      return (
                        <tr key={v.sha || v.version || i}>
                          <td><strong>{v.version}</strong>{isActive && <span className="badge badge-green" style={{ marginLeft: 6 }}>active</span>}</td>
                          <td className="mono small">{v.shortSha || (v.sha ? v.sha.slice(0, 7) : '—')}</td>
                          <td>{v.subject || '—'}</td>
                          <td className="muted">{author}</td>
                          <td className="muted small">{v.date ? new Date(v.date).toLocaleString() : '—'}</td>
                          <td><button className="btn btn-sm" onClick={() => loadVersion(v)}>Load</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
